import { OrganisationFeedItemDto } from '@careapp-group/ng-sdk';
import * as actions from '../actions';
import { ORGANISATION_CONTEXT_SWITCH } from '../actions/meta.actions';
import { Memos } from '../memos/memos.actions';
import { ReducerStateFactory } from '../state-factory.reducer';
import { Feed } from './feed.actions';

export const LOCAL_PREFEX = '__local';

class FeedState extends ReducerStateFactory<OrganisationFeedItemDto> {
  public reducer(state = this.initialState, action: actions.CAAction) {
    switch (action.type) {
      /*
       * Mostly we are returning initial state here, except we are
       * Pre-emptively setting the feed loading state to true so that
       * The feed page doesn't show the "nothing to see here" message
       * Until the request feed action is dispatched
       */
      case ORGANISATION_CONTEXT_SWITCH:
        return {
          ...state,
          entities: {},
          ids: [],
          selectedId: null,
          paginators: {
            feed: {
              loading: true,
              complete: false,
              ids: [],
            },
          },
          loading: true,
        };
      /**
       * Pre-emptively adds a newly created Memo or check in to the top of the feed.
       * This ensures that the newly created item is immediately visible in the feed
       * after creation, without having to wait for the feed to refresh, which can
       * take several seconds depending on network conditions.
       *
       * This memo will be removed from the feed when the next All Paginated event
       * occurs.
       *
       * Note also we are only using one static ID for the client-side temporary memo
       * to prevent the buildup of these as more and more memos are added.
       */
      case Memos.RESPONSE_SUCCESS_CREATE: {
        const localId = this.generateLocalId(action.payload, 'memo');
        const ids = Array.from(new Set([...state.ids, localId]));
        const entities = {
          ...state.entities,
          [localId]: {
            ...action.payload,
            id: localId,
            relatesToModel: 'Memo',
            type: 'memoCreated',
            relatesToId: action.payload.id,
            relatesTo: { ...action.payload },
          },
        };
        let paginators = { ...state.paginators };
        if (state.paginators.feed) {
          const pageIds = [localId, ...state.paginators.feed.ids];
          const pageFeed = { ...state.paginators.feed, ids: pageIds };
          paginators = { ...state.paginators, feed: pageFeed };
        }
        return {
          ...state,
          ids: ids,
          entities: entities,
          paginators,
        };
      }
      case actions.CheckIns.ResponseSuccessCreateAction.type: {
        const localId = this.generateLocalId(action.payload, 'checkIn');
        const ids = Array.from(new Set([...state.ids, localId]));
        const entities = {
          ...state.entities,
          [localId]: {
            ...action.payload,
            id: localId,
            relatesToModel: 'CheckIn',
            type: 'checkedIn',
            relatesToId: action.payload.id,
            relatesTo: { ...action.payload },
          },
        };
        let paginators = { ...state.paginators };
        if (state.paginators.feed) {
          const pageIds = [localId, ...state.paginators.feed.ids];
          const pageFeed = { ...state.paginators.feed, ids: pageIds };
          paginators = { ...state.paginators, feed: pageFeed };
        }
        return {
          ...state,
          ids: ids,
          entities: entities,
          paginators,
        };
      }
      // clear local entities from store if the first page of results if fetched
      case Feed.RESPONSE_SUCCESS_ALL_PAGINATED: {
        const { filter } = action.payload;
        const result = super.reducer(state, action);
        if (filter.skip === 0) {
          // ensure the entity exists, may be undefined in cases of old store versions of local data
          result.ids = result.ids.filter(
            (id) => !id.startsWith(LOCAL_PREFEX) && result.entities[id] && result.entities[id]
          );
          result.entities = result.ids
            .map((id) => result.entities[id])
            .reduce((acc: any, entity: any) => ({ ...acc, [entity.id]: entity }), {});
        }
        return result;
      }
    }
    return super.reducer(state, action);
  }

  private generateLocalId(item: { id: string }, type: string) {
    return `${LOCAL_PREFEX}.${type}.${item.id}`;
  }
}

export const state = new FeedState(Feed);

export function reducer(...args) {
  return state.reducer.apply(state, args);
}

// We don't want to persist the feed contents, but we do want the key to be there
export function getPersisted(segment: any): any {
  return state.initialState;
}
